<template>
  <div class="space-y-6 pt-4">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Analyse der Artikelperformance</h3>
      <MonthYearRangePicker v-model="dateRange" />
    </div>

    <div v-if="loading" class="flex justify-center py-20">
      <UIcon name="i-heroicons-arrow-path" class="w-8 h-8 animate-spin text-primary-500" />
    </div>

    <div v-else class="space-y-6">
      <!-- KPIs -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        <UCard v-for="kpi in kpis" :key="kpi.label" :ui="{ body: 'p-4' }">
          <div class="text-sm text-gray-500 dark:text-gray-400 font-medium line-clamp-1" :title="kpi.label">
            {{ kpi.label }}
          </div>
          <div class="text-xl font-extrabold mt-2" :style="kpi.color ? { color: kpi.color } : {}">
            {{ kpi.value }}
          </div>
        </UCard>
      </div>

      <!-- Monthly Purchase/Sales Chart -->
      <UCard
        :ui="{
          body: isChartCollapsed ? 'p-0' : 'p-4 flex flex-col',
          root: 'flex flex-col overflow-hidden',
        }"
      >
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="font-bold">Bezug/Absatz pro Monat</h3>
            <UButton
              :icon="isChartCollapsed ? 'i-heroicons-chevron-down' : 'i-heroicons-chevron-up'"
              color="gray"
              variant="ghost"
              @click="isChartCollapsed = !isChartCollapsed"
            />
          </div>
        </template>
        <div v-show="!isChartCollapsed" class="h-[400px] w-full">
          <DualAxisChart
            :data-source="chartDataset"
            argument-field="month"
            :bar-series="[
              { valueField: 'purchaseMge', name: 'Einkauf Menge', color: PURCHASE_COLOR },
              { valueField: 'salesMge', name: 'Verkauf Menge', color: SALES_COLOR },
            ]"
            :line-series="[
              { valueField: 'purchaseValue', name: 'Einkauf Wert', color: PURCHASE_VALUE_COLOR },
              { valueField: 'salesValue', name: 'Verkauf Wert', color: SALES_VALUE_COLOR },
              { valueField: 'avgPurchaseValue', name: 'Ø EK Wert', color: AVG_PURCHASE_COLOR },
              { valueField: 'avgSalesValue', name: 'Ø VK Wert', color: AVG_SALES_COLOR },
            ]"
            bar-axis-title="Menge"
            line-axis-title="Wert (EUR)"
          />
        </div>
      </UCard>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Suppliers List -->
        <UCard
          :ui="{
            body: isSuppliersCollapsed ? 'p-0' : 'p-4 flex flex-col',
            root: 'flex flex-col overflow-hidden',
          }"
          :class="{ 'h-fit': isSuppliersCollapsed, 'h-[500px]': !isSuppliersCollapsed }"
        >
          <template #header>
            <div class="flex items-center justify-between">
              <h3 class="font-bold">Lieferanten</h3>
              <UButton
                :icon="isSuppliersCollapsed ? 'i-heroicons-chevron-down' : 'i-heroicons-chevron-up'"
                color="gray"
                variant="ghost"
                @click="isSuppliersCollapsed = !isSuppliersCollapsed"
              />
            </div>
          </template>
          <div v-show="!isSuppliersCollapsed" class="flex-1 overflow-hidden h-[400px]">
            <UTable
              :columns="supplierColumns"
              :data="supplierList"
              class="w-full"
              :ui="{
                tr: 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors',
              }"
              sticky
              @select="onSupplierClick"
            >
              <template #totalValue-cell="{ row }">
                {{ formatCurrency((row.original as AggregationRow).totalValue) }}
              </template>
            </UTable>
          </div>
        </UCard>

        <!-- Customers List -->
        <UCard
          :ui="{
            body: isCustomersCollapsed ? 'p-0' : 'p-4 flex flex-col',
            root: 'flex flex-col overflow-hidden',
          }"
          :class="{ 'h-fit': isCustomersCollapsed, 'h-[500px]': !isCustomersCollapsed }"
        >
          <template #header>
            <div class="flex items-center justify-between">
              <h3 class="font-bold">Kunden</h3>
              <UButton
                :icon="isCustomersCollapsed ? 'i-heroicons-chevron-down' : 'i-heroicons-chevron-up'"
                color="gray"
                variant="ghost"
                @click="isCustomersCollapsed = !isCustomersCollapsed"
              />
            </div>
          </template>
          <div v-show="!isCustomersCollapsed" class="flex-1 overflow-hidden h-[400px]">
            <UTable
              :columns="customerColumns"
              :data="customerList"
              class="w-full"
              :ui="{
                tr: 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors',
              }"
              sticky
              @select="onCustomerClick"
            >
              <template #totalValue-cell="{ row }">
                {{ formatCurrency((row.original as AggregationRow).totalValue) }}
              </template>
            </UTable>
          </div>
        </UCard>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import DualAxisChart from '@/components/DualAxisChart.vue'
import MonthYearRangePicker from '@/components/MonthYearRangePicker.vue'
import { useAuthStore } from '@/stores/auth'

interface HistoryRow {
  fldArtNr: string
  fldAdrNr: number
  acoGPreisBG?: {
    totalNetAmount: number
  }
  fldDat: string
  fldMge: number
  rowAdrNr?: {
    fnIsVendor: boolean
    rowReAnsNr?: {
      fldNa2: string
    }
  }
}

interface AggregationRow {
  adrNr: number
  name: string
  totalMge: number
  totalValue: number
}

const props = defineProps<{
  fldArtNr: string
}>()

const PURCHASE_COLOR = '#dc2626'
const SALES_COLOR = '#2563eb'
const PROFIT_COLOR = '#10b981'
const PURCHASE_VALUE_COLOR = '#f87171'
const SALES_VALUE_COLOR = '#60a5fa'
const AVG_PURCHASE_COLOR = '#991b1b'
const AVG_SALES_COLOR = '#1e40af'

const router = useRouter()
const authStore = useAuthStore()

const customerNo = computed(() => authStore.userInfo?.activeTenant?.customerNo || '')
const tenantName = computed(() => authStore.userInfo?.activeTenant?.tenantName || '')

const loading = ref(true)

// Collapsible state
const isChartCollapsed = ref(false)
const isSuppliersCollapsed = ref(false)
const isCustomersCollapsed = ref(false)

const dateRange = ref({
  start: `${new Date().getFullYear()}-01`,
  end: `${new Date().getFullYear()}-12`,
})

const historyData = ref<HistoryRow[]>([])
const stockLevel = ref(0)
const articleKuBez = ref('')

const fetchData = async () => {
  if (!props.fldArtNr) return
  loading.value = true
  try {
    if (customerNo.value && tenantName.value) {
    }

    const historyQuery = `
      query tblHistory {
        tblHistory {
          rowsRead(
            allBetween: {
              byArtNr: { kf1ArtNr: { string: "${props.fldArtNr}" } }
            }
          ) {
            fldArtNr
            fldAdrNr
            acoGPreisBG {
              totalNetAmount
            }
            fldDat
            fldMge
            rowAdrNr {
              fnIsVendor
              rowReAnsNr {
                fldNa2
              }
            }
          }
        }
      }
    `

    const productQuery = `
      query TblProducts {
        tblProducts {
          rowRead(kf1ArtNr: { string: "${props.fldArtNr}" }) {
            fldLagMge
            fldKuBez1
          }
        }
      }
    `

    const [historyRes, productRes] = await Promise.all([
      axios.post(
        '/api/microtech/v1/proxy/graphql',
        { query: historyQuery },
        {
          headers: {
            'x-customer-no': customerNo.value,
            'x-tenant-name': tenantName.value,
          },
        },
      ),
      axios.post(
        '/api/microtech/v1/proxy/graphql',
        { query: productQuery },
        {
          headers: {
            'x-customer-no': customerNo.value,
            'x-tenant-name': tenantName.value,
          },
        },
      ),
    ])

    const historyDataRaw = historyRes.data?.data?.tblHistory?.rowsRead
    if (historyRes.data?.errors) {
      console.error('GraphQL Errors in History Query:', historyRes.data.errors)
    }
    historyData.value = historyDataRaw || []

    const productData = productRes.data?.data?.tblProducts?.rowRead
    if (productRes.data?.errors) {
      console.error('GraphQL Errors in Product Query:', productRes.data.errors)
    }
    stockLevel.value = productData?.fldLagMge || 0
    articleKuBez.value = productData?.fldKuBez1 || ''
  } catch (error) {
    console.error('Failed to fetch article dashboard data:', error)
  } finally {
    loading.value = false
  }
}

const filteredHistory = computed(() => {
  const [startYear, startMonth] = dateRange.value.start.split('-').map(Number)
  const [endYear, endMonth] = dateRange.value.end.split('-').map(Number)
  const startIso = `${startYear}-${startMonth.toString().padStart(2, '0')}-01`
  const endIsoLimit = `${endYear}-${endMonth.toString().padStart(2, '0')}-99`

  return historyData.value.filter((row) => row.fldDat >= startIso && row.fldDat <= endIsoLimit)
})

const totalPurchaseValue = computed(() => {
  return filteredHistory.value
    .filter((row) => row.rowAdrNr?.fnIsVendor === true)
    .reduce((sum, row) => sum + (row.acoGPreisBG?.totalNetAmount || 0), 0)
})

const totalSalesValue = computed(() => {
  return filteredHistory.value
    .filter((row) => row.rowAdrNr?.fnIsVendor === false)
    .reduce((sum, row) => sum + (row.acoGPreisBG?.totalNetAmount || 0), 0)
})

const totalProfit = computed(() => {
  return totalSalesValue.value - totalPurchaseValue.value
})

const avgPurchasePrice = computed(() => {
  const purchaseEntries = filteredHistory.value.filter((row) => row.rowAdrNr?.fnIsVendor === true)
  const sumPrice = purchaseEntries.reduce((sum, row) => sum + (row.acoGPreisBG?.totalNetAmount || 0), 0)
  const sumMge = purchaseEntries.reduce((sum, row) => sum + (row.fldMge || 0), 0)
  return sumMge > 0 ? sumPrice / sumMge : 0
})

const avgSalesPrice = computed(() => {
  const salesEntries = filteredHistory.value.filter((row) => row.rowAdrNr?.fnIsVendor === false)
  const sumPrice = salesEntries.reduce((sum, row) => sum + (row.acoGPreisBG?.totalNetAmount || 0), 0)
  const sumMge = salesEntries.reduce((sum, row) => sum + (row.fldMge || 0), 0)
  return sumMge > 0 ? sumPrice / sumMge : 0
})

const kpis = computed(() => [
  { label: 'Kurzbezeichnung', value: articleKuBez.value || '-' },
  {
    label: 'Gesamteinkaufswert',
    value: formatCurrency(totalPurchaseValue.value),
    color: PURCHASE_VALUE_COLOR,
  },
  {
    label: 'Gesamtverkaufswert',
    value: formatCurrency(totalSalesValue.value),
    color: SALES_VALUE_COLOR,
  },
  { label: 'Gewinn', value: formatCurrency(totalProfit.value), color: PROFIT_COLOR },
  {
    label: `Ø EK`,
    value: formatCurrency(avgPurchasePrice.value),
  },
  {
    label: `Ø VK`,
    value: formatCurrency(avgSalesPrice.value),
  },
  { label: 'Bestand', value: stockLevel.value.toString() },
])

const chartDataset = computed(() => {
  const [startYear, startMonth] = dateRange.value.start.split('-').map(Number)
  const [endYear, endMonth] = dateRange.value.end.split('-').map(Number)

  const data: Record<string, unknown>[] = []

  let currentYear = startYear
  let currentMonth = startMonth

  while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
    const monthLabel = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`
    const monthIsoStart = `${monthLabel}-01`
    const monthIsoEnd = `${monthLabel}-99`

    const monthRows = filteredHistory.value.filter((row) => row.fldDat >= monthIsoStart && row.fldDat <= monthIsoEnd)

    const purchaseMge = monthRows.filter((r) => r.rowAdrNr?.fnIsVendor === true).reduce((sum, r) => sum + (r.fldMge || 0), 0)
    const salesMge = monthRows.filter((r) => r.rowAdrNr?.fnIsVendor === false).reduce((sum, r) => sum + (r.fldMge || 0), 0)

    const purchaseValue = monthRows.filter((r) => r.rowAdrNr?.fnIsVendor === true).reduce((sum, r) => sum + (r.acoGPreisBG?.totalNetAmount || 0), 0)
    const salesValue = monthRows.filter((r) => r.rowAdrNr?.fnIsVendor === false).reduce((sum, r) => sum + (r.acoGPreisBG?.totalNetAmount || 0), 0)

    const avgPurchaseValue = purchaseMge > 0 ? purchaseValue / purchaseMge : 0
    const avgSalesValue = salesMge > 0 ? salesValue / salesMge : 0

    data.push({
      month: monthLabel,
      purchaseMge,
      salesMge,
      purchaseValue,
      salesValue,
      avgPurchaseValue,
      avgSalesValue,
    })

    currentMonth++
    if (currentMonth > 12) {
      currentMonth = 1
      currentYear++
    }
  }

  return data
})

const supplierList = computed(() => {
  const aggregation: Record<string, AggregationRow> = {}
  filteredHistory.value
    .filter((row) => row.rowAdrNr?.fnIsVendor === true)
    .forEach((row) => {
      const id = row.fldAdrNr.toString()
      if (!aggregation[id]) {
        aggregation[id] = {
          adrNr: row.fldAdrNr,
          name: row.rowAdrNr?.rowReAnsNr?.fldNa2 || '',
          totalMge: 0,
          totalValue: 0,
        }
      }
      aggregation[id].totalMge += row.fldMge || 0
      aggregation[id].totalValue += row.acoGPreisBG?.totalNetAmount || 0
    })
  return Object.values(aggregation).sort((a, b) => b.totalValue - a.totalValue)
})

const customerList = computed(() => {
  const aggregation: Record<string, AggregationRow> = {}
  filteredHistory.value
    .filter((row) => row.rowAdrNr?.fnIsVendor === false)
    .forEach((row) => {
      const id = row.fldAdrNr.toString()
      if (!aggregation[id]) {
        aggregation[id] = {
          adrNr: row.fldAdrNr,
          name: row.rowAdrNr?.rowReAnsNr?.fldNa2 || '',
          totalMge: 0,
          totalValue: 0,
        }
      }
      aggregation[id].totalMge += row.fldMge || 0
      aggregation[id].totalValue += row.acoGPreisBG?.totalNetAmount || 0
    })
  return Object.values(aggregation).sort((a, b) => b.totalValue - a.totalValue)
})

const supplierColumns = [
  { accessorKey: 'adrNr', header: 'AdrNr' },
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'totalMge', header: 'Menge' },
  { accessorKey: 'totalValue', header: 'Wert', id: 'totalValue' },
]

const customerColumns = [
  { accessorKey: 'adrNr', header: 'AdrNr' },
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'totalMge', header: 'Menge' },
  { accessorKey: 'totalValue', header: 'Wert', id: 'totalValue' },
]

const onSupplierClick = (_event: Event, row: { original: AggregationRow }) => {
  const rowData = row.original
  if (!rowData) return
  router.push({
    name: 'BiSupplierAnalysis',
    query: {
      start: dateRange.value.start,
      end: dateRange.value.end,
      fldAdrNr: rowData.adrNr,
    },
  })
}

const onCustomerClick = (_event: Event, row: { original: AggregationRow }) => {
  const rowData = row.original
  if (!rowData) return
  router.push({
    name: 'BiCustomerDashboard',
    query: {
      start: dateRange.value.start,
      end: dateRange.value.end,
      fldAdrNr: rowData.adrNr,
    },
  })
}

const formatCurrency = (val: number) => {
  if (Math.abs(val) < 0.01) return '0 €'
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)
}

watch(() => props.fldArtNr, () => {
  fetchData()
})

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    fetchData()
  }

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      fetchData()
    }
  })
})
</script>
